import React, { useEffect, useState } from "react";
import { motion } from "motion/react";
import {
  Check,
  Sparkles,
  Shield,
  Heart,
  Clock,
  Zap,
  Loader2,
} from "lucide-react";
import { getPlans, savePlansToLocalStorage, type Plan } from "../../api/plans";
import { toast } from "sonner";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { updateUser } from "../../store/slices/authSlice";
import apiClient from "../../api/apiClient";

export default function Pricing() {
  const { user } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [purchasingId, setPurchasingId] = useState<string | null>(null);

  const handlePurchase = async (plan: any) => {
    if (!user) {
      toast.error("Please login to purchase a plan");
      return;
    }

    if (plan.price === 0) {
      toast.info("This is your free trial pack, already active!");
      return;
    }

    setPurchasingId(plan._id || plan.name);
    try {
      // 1. Create Payment Link on Backend
      const res = await apiClient.post("/users/create-payment-link", {
        amount: plan.price,
        planId: plan._id || plan.name,
      });

      if (!res.data.success || !res.data.paymentLink) {
        throw new Error("Failed to create payment link");
      }

      // 2. Redirect to Payment Link
      window.location.href = res.data.paymentLink;
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Something went wrong. Please try again.",
      );
    } finally {
      // Redirection will happen if successful
      setPurchasingId(null);
    }
  };

  useEffect(() => {
    async function fetchPlans() {
      try {
        const res = await getPlans();
        let finalPlans: Plan[] = [];
        if (res.success && res.data.length > 0) {
          finalPlans = res.data.sort((a: any, b: any) => a.price - b.price);
        } else {
          // Fallback to attractive defaults if API is empty
          finalPlans = [
            {
              name: "🎁 Gift Pack (D)",
              price: 0,
              durationUnit: "first 5 mins",
              description: "Try FeelTalk at zero cost",
              features: [
                "✅ 5 Minutes FREE!",
                "👉 No Upfront Payment",
                "💰 Try Before You Buy",
                "✔ Instant Matching",
              ],
              buttonText: "Start Free Now",
              isPopular: false,
            },
            {
              name: "💖 Heart Pack (B)",
              price: 250,
              durationUnit: "50 Minutes",
              description: "The most loved starting choice",
              features: [
                "✅ 50 Min Talk Time",
                "👉 ₹5 Per Minute Only",
                "💰 Priority Listeners",
                "✔ Lifetime Validity",
                "🔒 Fully Anonymous",
              ],
              buttonText: "Get Started Now",
              isPopular: true,
            },
            {
              name: "💎 Diamond Pack (C)",
              price: 1000,
              durationUnit: "225 Minutes",
              description: "Best for regular support",
              features: [
                "✅ 225 Min (25m Bonus! 🎁)",
                "👉 Cheapest Rate per Min",
                "💰 Expert Listeners",
                "✔ Dedicated Support",
                "📈 Emotional Insights",
              ],
              buttonText: "Claim Max Value",
              isPopular: false,
            },
          ] as any;
        }
        setPlans(finalPlans);
        savePlansToLocalStorage(finalPlans);
      } catch (error) {
        console.error("Failed to load plans", error);
      } finally {
        setLoading(false);
      }
    }
    fetchPlans();
  }, []);

  if (user?.role === "listener") {
    return null;
  }

  return (
    <section
      id="pricing"
      className="py-20 px-6 bg-gradient-to-b from-purple-50 to-white"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-block px-4 py-1 bg-purple-100 text-purple-600 rounded-full text-sm font-bold uppercase tracking-widest mb-4">
            Talking Side
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Pick Your Pricing Model
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Choose how you want to connect with listeners.
          </p>
        </motion.div>

        {loading ? (
          <div className="text-center py-20">
            <div className="animate-spin w-10 h-10 border-4 border-purple-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-500 font-medium">
              Loading pricing options...
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-8">
            {plans.map((plan, index) => {
              const isActive = (plan.price === 0 && user?.role === "talking" && !user?.isPremium && !user?.freeTrialUsed) ||
                               (plan.price > 0 && user?.isPremium && (user?.activePlanId === (plan._id || plan.name) || user?.activePlanId?.includes(plan.name) || (plan._id || plan.name).includes(user?.activePlanId || 'UNDEF')));
              
              return (
                <motion.div
                  key={plan._id || index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className={`relative p-8 rounded-[40px] border transition-all ${
                    plan.isPopular
                      ? "bg-gradient-to-br from-purple-600 to-pink-500 text-white border-transparent shadow-2xl scale-105 z-10"
                      : isActive 
                         ? "bg-white border-purple-500 shadow-2xl shadow-purple-500/10 scale-[1.02] border-2"
                         : "bg-white border-purple-100 shadow-xl hover:shadow-2xl hover:scale-[1.02]"
                  }`}
                >
                  {isActive && (
                    <div className={`absolute top-4 right-8 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest shadow-sm z-20 ${
                      plan.isPopular ? "bg-white/20 text-white border border-white/30" : "bg-purple-100 text-purple-600 border border-purple-200"
                    }`}>
                      Current Plan
                    </div>
                  )}

                  <div className="mb-6">
                    <div className="flex items-center gap-2 mb-2">
                      {plan.icon && <span className="text-2xl">{plan.icon}</span>}
                      <h3
                        className={`text-2xl font-bold ${plan.isPopular ? "text-white" : "text-gray-900"}`}
                      >
                        {plan.name}
                      </h3>
                    </div>
                    <p
                      className={`text-sm ${plan.isPopular ? "text-purple-100" : "text-gray-600"}`}
                    >
                      {plan.description}
                    </p>
                  </div>

                  <div className="mb-8">
                    <div className="flex items-baseline gap-1">
                      <span
                        className={`text-5xl font-extrabold ${plan.isPopular ? "text-white" : "text-gray-900"}`}
                      >
                        ₹{plan.price}
                      </span>
                      <span
                        className={`font-medium ${plan.isPopular ? "text-purple-100" : "text-gray-500"}`}
                      >
                        /{plan.durationUnit}
                      </span>
                    </div>
                  </div>

                  <button
                    onClick={() => handlePurchase(plan)}
                    disabled={purchasingId === (plan._id || plan.name)}
                    className={`w-full py-4 px-6 rounded-full font-bold text-lg transition-all mb-8 active:scale-95 flex items-center justify-center gap-2 ${
                      plan.isPopular
                        ? "bg-white text-purple-600 hover:bg-purple-50"
                        : isActive
                          ? "bg-purple-600 text-white shadow-lg"
                          : plan.price === 0 &&
                            user?.role === "talking" &&
                            !user?.isPremium &&
                            !user?.freeTrialUsed
                            ? "bg-green-100/50 text-green-700 cursor-default border-2 border-green-200"
                            : "bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:shadow-lg shadow-purple-200"
                    }`}
                  >
                    {purchasingId === (plan._id || plan.name) ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : isActive ? (
                      "PLAN ACTIVE"
                    ) : plan.price === 0 &&
                      user?.role === "talking" &&
                      !user?.isPremium &&
                      !user?.freeTrialUsed ? (
                      "CURRENTLY ACTIVE"
                    ) : (
                      plan.buttonText || (plan as any).cta || "Buy Now"
                    )}
                  </button>

                  <ul className="space-y-4">
                    {plan.features.map((feature: string, i: number) => (
                      <li key={i} className="flex items-start gap-3">
                        <span className="text-xl leading-none">
                          {feature.match(
                            /^[\u{1F300}-\u{1F9FF}\u{2600}-\u{26FF}✅👉✔💰]/u,
                          ) ? null : (
                            <Check
                              className={`w-5 h-5 flex-shrink-0 mt-0.5 ${plan.isPopular ? "text-purple-100" : "text-purple-600"}`}
                            />
                          )}
                        </span>
                        <span
                          className={`text-sm font-medium ${plan.isPopular ? "text-white" : "text-gray-700"}`}
                        >
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Trust Badges - Merged from Page style for 'Frontend side Home page' feel */}
        <div className="max-w-4xl mx-auto mt-20 grid grid-cols-2 md:grid-cols-4 gap-8 text-center border-t border-purple-100 pt-12">
          <div className="flex flex-col items-center gap-2">
            <Shield className="w-8 h-8 text-purple-500" />
            <p className="text-sm font-semibold text-gray-700">
              100% Encrypted
            </p>
          </div>
          <div className="flex flex-col items-center gap-2">
            <Heart className="w-8 h-8 text-pink-500" />
            <p className="text-sm font-semibold text-gray-700">
              Expert Listeners
            </p>
          </div>
          <div className="flex flex-col items-center gap-2">
            <Clock className="w-8 h-8 text-blue-500" />
            <p className="text-sm font-semibold text-gray-700">24/7 Support</p>
          </div>
          <div className="flex flex-col items-center gap-2">
            <Zap className="w-8 h-8 text-yellow-500" />
            <p className="text-sm font-semibold text-gray-700">
              Instant Access
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
